import { useState, useMemo, useEffect, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface PaymentRecord {
  id: string;
  team_name: string;
  registration_id: string;
  amount: number;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  status: string;
  created_at: string;
  college_name: string;
}

const statusColor = (s: string) => {
  switch (s) {
    case 'paid': return 'bg-accent-100 text-accent-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    case 'failed': return 'bg-red-100 text-red-700';
    default: return 'bg-background-200 text-foreground-500';
  }
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: pays, error: payErr } = await supabase.from('payments').select('id, team_id, razorpay_order_id, razorpay_payment_id, amount, status, created_at').order('created_at', { ascending: false });
      if (payErr) throw payErr;

      if (!pays || pays.length === 0) { setPayments([]); setLoading(false); return; }

      const teamIds = [...new Set((pays || []).map((p) => p.team_id))];
      const { data: teams } = await supabase.from('teams').select('id, team_name, registration_id, college_name').in('id', teamIds);
      const teamMap: Record<string, { team_name: string; registration_id: string; college_name: string }> = {};
      (teams || []).forEach((t) => { teamMap[t.id] = { team_name: t.team_name, registration_id: t.registration_id, college_name: t.college_name }; });

      setPayments((pays || []).map((p) => ({
        id: p.id,
        team_name: teamMap[p.team_id]?.team_name || 'Unknown Team',
        registration_id: teamMap[p.team_id]?.registration_id || '—',
        amount: Number(p.amount),
        razorpay_order_id: p.razorpay_order_id,
        razorpay_payment_id: p.razorpay_payment_id,
        status: p.status,
        created_at: p.created_at,
        college_name: teamMap[p.team_id]?.college_name || '—',
      })));
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const filtered = useMemo(() => {
    let result = payments;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((p) => p.team_name.toLowerCase().includes(q) || p.registration_id.toLowerCase().includes(q) || (p.razorpay_order_id || '').toLowerCase().includes(q));
    }
    if (statusFilter) result = result.filter((p) => p.status === statusFilter);
    return result;
  }, [payments, search, statusFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalRevenue = filtered.filter((p) => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);

  return (
    <AuthGuard requiredRole={['admin', 'organizer']}>
      <AdminLayout title="Payments" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Payments' }]}>
        {fetchError && (
          <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load: {fetchError}. <button onClick={fetchPayments} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="p-4 rounded-xl border border-background-200/70 bg-background-50">
            <p className="text-xs text-foreground-500">Total Revenue</p>
            <p className="text-xl font-bold text-foreground-900">{loading ? '—' : `₹${totalRevenue.toLocaleString()}`}</p>
          </div>
          <div className="p-4 rounded-xl border border-background-200/70 bg-background-50">
            <p className="text-xs text-foreground-500">Total Payments</p>
            <p className="text-xl font-bold text-foreground-900">{loading ? '—' : filtered.length}</p>
          </div>
          <div className="p-4 rounded-xl border border-background-200/70 bg-background-50">
            <p className="text-xs text-foreground-500">Paid</p>
            <p className="text-xl font-bold text-accent-700">{loading ? '—' : filtered.filter((p) => p.status === 'paid').length}</p>
          </div>
          <div className="p-4 rounded-xl border border-background-200/70 bg-background-50">
            <p className="text-xs text-foreground-500">Pending/Failed</p>
            <p className="text-xl font-bold text-amber-700">{loading ? '—' : filtered.filter((p) => p.status !== 'paid').length}</p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 mb-5">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative w-full sm:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="Search by team or order ID..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      {['Team', 'Reg ID', 'Amount', 'Razorpay Order ID', 'Payment ID', 'Status', 'Date', 'Receipt'].map((label) => (
                        <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((p) => (
                      <tr key={p.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">{p.team_name}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs font-mono">{p.registration_id}</td>
                        <td className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">₹{p.amount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs font-mono">{p.razorpay_order_id || '—'}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs font-mono">{p.razorpay_payment_id || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusColor(p.status)}`}>
                            {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{new Date(p.created_at).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button className="px-3 py-1.5 rounded-lg text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-download-line mr-1"></i>Receipt
                          </button>
                        </td>
                      </tr>
                    ))}
                    {paginated.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-foreground-400">No payments found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-background-200/70">
                  <p className="text-xs text-foreground-400">Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, filtered.length)} of {filtered.length}</p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"><i className="ri-arrow-left-s-line text-sm"></i></button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                      <button key={pg} onClick={() => setCurrentPage(pg)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${pg === currentPage ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100'}`}>{pg}</button>
                    ))}
                    <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"><i className="ri-arrow-right-s-line text-sm"></i></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </AuthGuard>
  );
}